import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import
{
    BoxArrowUpRight,
    Database,
    KeyFill,
    PlayCircle,
    Search,
    PlusLg,
    Trash3,
    PencilSquare,
    XLg,
    ExclamationTriangleFill
} from 'react-bootstrap-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import
{
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    previewOrganizationDeletion,
    OrganizationItem,
    OrganizationFilterParams,
    OrganizationDeletionPreview
} from '../api/organizations';
import { useAuth } from '../contexts/AuthContext';
import CustomDropdown from '../components/CustomDropdown';
import { getGuidedTourStepIndexById } from '../tour/dashboardGuidedTourConfig';
import { ORGANIZATION_CREATE_MODAL_EVENT } from '../tour/GuidedTourContext';
import {
    GUIDED_TOUR_STORAGE_KEYS,
    getTourStorageValue,
    markOnboardingOrganizationCreated,
} from '../tour/guidedTourStorage';
import { isSuperAdminUser } from '../utils/roles';
import './OrganizationManagement.css';

/* ============================================================
   OrganizationManagement.tsx
   Admin page for managing organizations.
   Table: ORGANIZATION | TYPE | SIZE | STATUS | CREATED AT | ACTION
   ============================================================ */

/* ---------- Constants ---------- */

const PAGE_SIZE = 10;

const ORG_TYPE_OPTIONS = ['Enterprise', 'SMB', 'Startup', 'Non-Profit', 'Government', 'Education', 'Other'];
const ORG_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

/* ---------- Type definitions ---------- */

interface Notification
{
    type: 'success' | 'danger';
    message: string;
}

/* ---------- Helpers ---------- */

function formatDate(iso: string | null | undefined): string
{
    if (!iso) { return '—'; }
    try
    {
        return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    catch
    {
        return iso;
    }
}

function getOrgInitials(name: string): string
{
    const words = name.trim().split(/\s+/);
    if (words.length >= 2)
    {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
}

function canViewOrganization(org: OrganizationItem, isSuperAdmin: boolean): boolean
{
    if (isSuperAdmin)
    {
        return true;
    }

    return !!org.permissions?.can_view_details;
}

/* ============================================================
   Main Component
   ============================================================ */

const OrganizationManagement: React.FC = () =>
{
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, updateUser } = useAuth();
    const isSuperAdmin = isSuperAdminUser(user);

    /* ---------- State: organizations list ---------- */
    const [items, setItems] = useState<OrganizationItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ---------- State: filters ---------- */
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [typeInput, setTypeInput] = useState('');
    const [appliedType, setAppliedType] = useState('');
    const [fromDateInput, setFromDateInput] = useState('');
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [toDateInput, setToDateInput] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [dateError, setDateError] = useState('');
    /* ---------- State: create modal ---------- */
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '',
        organization_type: '',
        organization_size: '',
        assign_free_token_quota: false,
    });
    const [createErrors, setCreateErrors] = useState<{ [k: string]: string }>({});

    /* ---------- State: edit modal ---------- */
    const [editTarget, setEditTarget] = useState<OrganizationItem | null>(null);
    const [editErrors, setEditErrors] = useState<{ [k: string]: string }>({});

    /* ---------- State: delete modal ---------- */
    const [deleteTarget, setDeleteTarget] = useState<OrganizationItem | null>(null);
    const [deletePreview, setDeletePreview] = useState<OrganizationDeletionPreview | null>(null);
    const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);

    /* ---------- Shared flags ---------- */
    const [saving, setSaving] = useState(false);
    const [notify, setNotify] = useState<Notification | null>(null);
    const tableColumnCount = 6;

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

    /* ---------- Data loading ---------- */

    async function loadOrganizations(): Promise<void>
    {
        setLoading(true);
        setError(null);
        try
        {
            const filters: OrganizationFilterParams = {};
            if (appliedSearch) filters.search = appliedSearch;
            if (appliedType) filters.organization_type = appliedType;
            if (appliedFromDate) filters.date_from = appliedFromDate;
            if (appliedToDate) filters.date_to = appliedToDate;
            const data = await fetchOrganizations(page, PAGE_SIZE, false, filters);
            setItems(data.items);
            setTotal(data.total);
        }
        catch (err: any)
        {
            setError(err.response?.data?.detail || 'Failed to load organizations');
        }
        finally
        {
            setLoading(false);
        }
    }

    useEffect(() =>
    {
        loadOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, appliedSearch, appliedType, appliedFromDate, appliedToDate]);

    useEffect(() =>
    {
        function handleOpenCreateModal(): void
        {
            setShowCreate(true);
        }

        window.addEventListener(ORGANIZATION_CREATE_MODAL_EVENT, handleOpenCreateModal);
        return () => window.removeEventListener(ORGANIZATION_CREATE_MODAL_EVENT, handleOpenCreateModal);
    }, []);

    /* ---------- Filter handlers ---------- */

    function applyFilters(): void
    {
        if ((fromDateInput && !toDateInput) || (!fromDateInput && toDateInput))
        {
            setDateError('Please select both a start date and an end date.');
            return;
        }
        setDateError('');
        setAppliedSearch(searchInput);
        setAppliedType(typeInput);
        setAppliedFromDate(fromDateInput);
        setAppliedToDate(toDateInput);
        setPage(1);
    }

    function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void
    {
        if (e.key === 'Enter') { applyFilters(); }
    }

    function handleClearFilters(): void
    {
        setSearchInput('');
        setTypeInput('');
        setFromDateInput('');
        setToDateInput('');
        setAppliedSearch('');
        setAppliedType('');
        setAppliedFromDate('');
        setAppliedToDate('');
        setDateError('');
        setPage(1);
    }

    /* ---------- Create ---------- */

    function validateCreate(): boolean
    {
        const errs: { [k: string]: string } = {};
        if (!createForm.name.trim())
        {
            errs.name = 'Organization name is required';
        }
        setCreateErrors(errs);
        return Object.keys(errs).length === 0;
    }

    async function handleCreate(): Promise<void>
    {
        if (!validateCreate()) { return; }
        setSaving(true);
        try
        {
            const organization = await createOrganization({
                name: createForm.name.trim(),
                organization_type: createForm.organization_type || undefined,
                organization_size: createForm.organization_size || undefined,
                ...(isSuperAdmin && createForm.assign_free_token_quota
                    ? { assign_free_token_quota: true }
                    : {}),
            });

            const existingOrganizations = Array.isArray(user?.organizations)
                ? user.organizations.filter((item) => item.id !== organization.id)
                : [];

            if (isSuperAdmin)
            {
                updateUser({
                    role: user?.role || 'superadmin',
                    organization_id: undefined,
                    organization_name: undefined,
                    organization_role: undefined,
                    active_organization_id: undefined,
                    organizations: existingOrganizations,
                });
            }
            else
            {
                updateUser({
                    role: 'admin',
                    organization_id: organization.id,
                    organization_name: organization.name,
                    organization_role: 'admin',
                    active_organization_id: organization.id,
                    organizations: [
                        { id: organization.id, name: organization.name, role: 'admin' },
                        ...existingOrganizations,
                    ],
                });
            }

            setNotify({ type: 'success', message: 'Organization created successfully' });
            setShowCreate(false);
            setCreateForm({ name: '', organization_type: '', organization_size: '', assign_free_token_quota: false });
            await loadOrganizations();

            const guidedTourRequested = searchParams.get('guidedTour') === '1'
                || getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested) === 'true';

            if (guidedTourRequested)
            {
                markOnboardingOrganizationCreated(getGuidedTourStepIndexById('billing-decision'));
                navigate('/billing?guidedTour=1');
            }
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to create organization' });
        }
        finally
        {
            setSaving(false);
        }
    }

    function handleCloseCreate(): void
    {
        setShowCreate(false);
        setCreateErrors({});
        setCreateForm({ name: '', organization_type: '', organization_size: '', assign_free_token_quota: false });
    }

    /* ---------- Edit ---------- */

    function validateEdit(): boolean
    {
        const errs: { [k: string]: string } = {};
        if (!editTarget?.name.trim())
        {
            errs.name = 'Organization name is required';
        }
        setEditErrors(errs);
        return Object.keys(errs).length === 0;
    }

    async function handleSaveEdit(): Promise<void>
    {
        if (!editTarget || !validateEdit()) { return; }
        setSaving(true);
        try
        {
            const updatePayload: Record<string, unknown> = {
                name: editTarget.name.trim(),
                organization_type: editTarget.organization_type || undefined,
                organization_size: editTarget.organization_size || undefined,
            };

            if (isSuperAdmin)
            {
                updatePayload.feature_test_execution_enabled = !!editTarget.feature_test_execution_enabled;
                updatePayload.feature_synthetic_data_enabled = !!editTarget.feature_synthetic_data_enabled;
                updatePayload.feature_byo_key_enabled = !!editTarget.feature_byo_key_enabled;
                updatePayload.feature_data_validation_agent_enabled = !!editTarget.feature_data_validation_agent_enabled;
                updatePayload.feature_exploratory_agent_enabled = !!editTarget.feature_exploratory_agent_enabled;
                updatePayload.feature_ritz_reservation_demo_enabled = !!editTarget.feature_ritz_reservation_demo_enabled;
            }

            await updateOrganization(editTarget.id, {
                ...updatePayload,
            });
            setNotify({ type: 'success', message: 'Organization updated successfully' });
            setEditTarget(null);
            await loadOrganizations();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to update organization' });
        }
        finally
        {
            setSaving(false);
        }
    }

    function handleCloseEdit(): void
    {
        setEditTarget(null);
        setEditErrors({});
    }

    /* ---------- Delete ---------- */

    async function openDeleteModal(organization: OrganizationItem): Promise<void>
    {
        setDeleteTarget(organization);
        setDeletePreview(null);
        setDeletePreviewLoading(true);
        try
        {
            setDeletePreview(await previewOrganizationDeletion(organization.id));
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to inspect organization jobs' });
            setDeleteTarget(null);
        }
        finally
        {
            setDeletePreviewLoading(false);
        }
    }

    async function handleConfirmDelete(): Promise<void>
    {
        if (!deleteTarget) { return; }
        if (deletePreviewLoading || !deletePreview) { return; }
        if (deletePreview.active_job_count > 0)
        {
            setNotify({
                type: 'danger',
                message: `Organization jobs are active. Cancel or stop jobs ${deletePreview.active_job_ids.join(', ')} before deleting ${deleteTarget.name}.`,
            });
            return;
        }
        setSaving(true);
        try
        {
            await deleteOrganization(deleteTarget.id);
            setNotify({ type: 'success', message: 'Organization deleted successfully' });
            setDeleteTarget(null);
            await loadOrganizations();
        }
        catch (err: any)
        {
            setNotify({ type: 'danger', message: err.response?.data?.detail || 'Failed to delete organization' });
        }
        finally
        {
            setSaving(false);
        }
    }

    /* ---------- Render: filter bar ---------- */

    function renderFilters(): React.ReactNode
    {
        const hasActiveFilters = !!(appliedSearch || appliedType || appliedFromDate || appliedToDate);

        return (
            <div className="om-filters">
                {/* Search by name */}
                <div className="om-filter-field om-filter-search">
                    <svg className="om-filter-search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    <input
                        className="om-filter-input om-filter-input-search"
                        type="text"
                        placeholder="Search by name…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                </div>

                {/* Type filter */}
                <div className="om-filter-field om-filter-dropdown">
                    <CustomDropdown
                        value={typeInput}
                        options={[
                            { value: '', label: 'All Types' },
                            ...ORG_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))
                        ]}
                        onChange={(v) => setTypeInput(v)}
                        placeholder="All Types"
                    />
                </div>

                {/* Date range filter */}
                <div className="om-filter-field om-filter-date filter-date-ph-wrap">
                    <input
                        className={`om-filter-date-input${!fromDateInput ? ' date-empty' : ''}${dateError ? ' om-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="From date"
                        value={fromDateInput}
                        onChange={(e) => { setFromDateInput(e.target.value); setDateError(''); }}
                    />
                    {!fromDateInput && <span className="filter-date-ph" aria-hidden="true">Start Date</span>}
                </div>
                <div className="om-filter-field om-filter-date filter-date-ph-wrap">
                    <input
                        className={`om-filter-date-input${!toDateInput ? ' date-empty' : ''}${dateError ? ' om-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="To date"
                        value={toDateInput}
                        onChange={(e) => { setToDateInput(e.target.value); setDateError(''); }}
                    />
                    {!toDateInput && <span className="filter-date-ph" aria-hidden="true">End Date</span>}
                </div>
                {dateError && <span className="om-filter-date-error">{dateError}</span>}

                {/* Search button */}
                <button
                    className="om-btn-search"
                    onClick={applyFilters}
                    type="button"
                    disabled={loading}
                >
                    Search
                </button>

                {/* Clear — only when filters are active */}
                {hasActiveFilters && (
                    <button
                        className="om-btn-clear-filters"
                        onClick={handleClearFilters}
                        type="button"
                    >
                        Clear
                    </button>
                )}
            </div>
        );
    }

    /* ---------- Render: notification ---------- */

    function renderNotification(): React.ReactNode
    {
        if (!notify) { return null; }
        const cls = notify.type === 'success' ? 'om-alert om-alert-success' : 'om-alert om-alert-danger';
        return (
            <div className={cls}>
                <span>{notify.message}</span>
                <button className="om-alert-dismiss" onClick={() => setNotify(null)} type="button">
                    Dismiss
                </button>
            </div>
        );
    }

    /* ---------- Render: page header ---------- */

    function renderHeader(): React.ReactNode
    {
        return (
            <div className="om-header">
                <h1 className="om-title">Organization Management</h1>
                <div className="om-header-actions">
                    <button
                        id="organization-management-create-action"
                        className="om-btn-create"
                        onClick={() => setShowCreate(true)}
                        type="button"
                    >
                        <PlusLg size={16} />
                        Create Organization
                    </button>
                </div>
            </div>
        );
    }

    /* ---------- Render: organizations table ---------- */

    function renderTableBody(): React.ReactNode
    {
        if (loading)
        {
            return (
                <tbody>
                    <tr className="om-loading-row">
                        <td colSpan={tableColumnCount}><Spinner animation="border" /></td>
                    </tr>
                </tbody>
            );
        }

        if (error)
        {
            return (
                <tbody>
                    <tr className="om-empty-row">
                        <td colSpan={tableColumnCount}>{error}</td>
                    </tr>
                </tbody>
            );
        }

        if (items.length === 0)
        {
            return (
                <tbody>
                    <tr className="om-empty-row">
                        <td colSpan={tableColumnCount}>No organizations found</td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {items.map((org) =>
                {
                    const initials = getOrgInitials(org.name);
                    const showViewButton = canViewOrganization(org, isSuperAdmin);

                    return (
                        <tr key={org.id}>
                            {/* Organization name + initials */}
                            <td data-label="Organization">
                                <div className="om-org-cell">
                                    <div className="om-org-avatar">
                                        {initials}
                                    </div>
                                    <div>
                                        <div className="om-org-name">{org.name}</div>
                                        <div className="om-org-id">ID: {org.id}</div>
                                    </div>
                                </div>
                            </td>

                            {/* Type */}
                            <td data-label="Type">
                                <span className="om-type-text">
                                    {org.organization_type || '—'}
                                </span>
                            </td>

                            {/* Size */}
                            <td data-label="Size">
                                <span className="om-size-text">
                                    {org.organization_size || '—'}
                                </span>
                            </td>

                            {/* Status */}
                            <td data-label="Status">
                                <span className="om-status om-status-active">
                                    Active
                                </span>
                            </td>

                            {/* Created at */}
                            <td data-label="Created At">
                                <span className="om-date-text">{formatDate(org.created_at)}</span>
                            </td>

                            {/* Actions */}
                            <td data-label="Actions" data-card-actions="true">
                                <div className="om-actions">
                                    {showViewButton ? (
                                        <button
                                            className="om-btn-view"
                                            onClick={() => navigate(`/admin/organizations/${org.id}`)}
                                            type="button"
                                        >
                                            <BoxArrowUpRight size={14} />
                                            View
                                        </button>
                                    ) : null}
                                    <button
                                        className="om-btn-edit"
                                        onClick={() => { setEditTarget({ ...org }); setEditErrors({}); }}
                                        title="Edit organization"
                                        type="button"
                                    >
                                        <PencilSquare size={14} />
                                        Edit
                                    </button>
                                    <button
                                        className="om-btn-remove"
                                        onClick={() => void openDeleteModal(org)}
                                        title="Delete organization"
                                        type="button"
                                    >
                                        <Trash3 size={14} />
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    function renderTable(): React.ReactNode
    {
        /* Calculate paging */
        const startItem = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
        const endItem = Math.min(page * PAGE_SIZE, total);

        const pageStart = Math.max(1, page - 2);
        const pageEnd = Math.min(totalPages, pageStart + 4);
        const pageNumbers: number[] = [];
        for (let p = pageStart; p <= pageEnd; p++) { pageNumbers.push(p); }

        return (
            <>
                <div className="om-table-card">
                    <table className="om-table" data-responsive-cards="true">
                        <thead>
                            <tr>
                                <th>Organization</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th>Status</th>
                                <th>Created At</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        {renderTableBody()}
                    </table>

                    {/* Footer */}
                    <div className="om-footer">
                        <span className="om-showing-text">
                            Showing {startItem}–{endItem} of {total} organizations
                        </span>
                        <div className="om-pagination">
                            <button
                                className="om-page-btn"
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                                type="button"
                            >
                                ← First
                            </button>
                            <button
                                className="om-page-btn"
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                type="button"
                            >
                                Prev
                            </button>
                            {pageNumbers.map((p) => (
                                <button
                                    key={p}
                                    className={`om-page-btn ${p === page ? 'om-page-active' : ''}`}
                                    onClick={() => setPage(p)}
                                    type="button"
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="om-page-btn"
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page === totalPages}
                                type="button"
                            >
                                Next
                            </button>
                            <button
                                className="om-page-btn"
                                onClick={() => setPage(totalPages)}
                                disabled={page === totalPages}
                                type="button"
                            >
                                Last →
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    /* ---------- Render: create modal ---------- */

    function renderCreateModal(): React.ReactNode
    {
        return (
            <Modal show={showCreate} onHide={handleCloseCreate} centered>
                <div className="om-modal-header">
                    <div className="om-modal-header-left">
                        <div className="om-modal-icon om-modal-icon-blue">
                            <PlusLg size={20} />
                        </div>
                        <div>
                            <h2 className="om-modal-title">Create Organization</h2>
                            <p className="om-modal-subtitle">Add a new organization to the system</p>
                        </div>
                    </div>
                    <button className="om-modal-close" onClick={handleCloseCreate} type="button" aria-label="Close">
                        <XLg size={18} />
                    </button>
                </div>

                <div className="om-modal-body">
                    {/* Name */}
                    <div id="organization-create-name" className="mb-3">
                        <label className="om-edit-label">Organization Name *</label>
                        <Form.Control
                            id="organization-create-name-input"
                            value={createForm.name}
                            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                            isInvalid={!!createErrors.name}
                            placeholder="e.g. Acme Corporation"
                        />
                        <Form.Control.Feedback type="invalid">{createErrors.name}</Form.Control.Feedback>
                    </div>

                    {/* Type + Size */}
                    <div className="row g-3 mb-3">
                        <div id="organization-create-type" className="col-md-6">
                            <label className="om-edit-label">Organization Type</label>
                            <CustomDropdown
                                value={createForm.organization_type}
                                options={ORG_TYPE_OPTIONS}
                                onChange={(selected) => setCreateForm({ ...createForm, organization_type: selected })}
                                placeholder="— Select type —"
                            />
                        </div>
                        <div id="organization-create-size" className="col-md-6">
                            <label className="om-edit-label">Organization Size</label>
                            <CustomDropdown
                                value={createForm.organization_size}
                                options={ORG_SIZE_OPTIONS}
                                onChange={(selected) => setCreateForm({ ...createForm, organization_size: selected })}
                                placeholder="— Select size —"
                            />
                        </div>
                    </div>

                    {isSuperAdmin ? (
                        <div className="om-free-quota-toggle mb-3">
                            <span>Assign active Free Plan tokens</span>
                            <button
                                id="organization-create-assign-free-quota"
                                type="button"
                                className={`om-platform-toggle ${createForm.assign_free_token_quota ? 'om-platform-toggle-on' : ''}`}
                                role="switch"
                                aria-checked={createForm.assign_free_token_quota}
                                aria-label="Assign active Free Plan tokens"
                                onClick={() => setCreateForm({
                                    ...createForm,
                                    assign_free_token_quota: !createForm.assign_free_token_quota,
                                })}
                            >
                                <span className="om-platform-toggle-knob" />
                            </button>
                        </div>
                    ) : null}

                    {/* Actions */}
                    <div className="om-modal-actions">
                        <button className="om-btn-modal-cancel" onClick={handleCloseCreate} type="button">
                            Cancel
                        </button>
                        <button
                            id="organization-create-submit"
                            className="om-btn-modal-confirm om-btn-confirm-blue"
                            onClick={handleCreate}
                            disabled={saving}
                            type="button"
                        >
                            {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                            Create Organization
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    /* ---------- Render: edit modal ---------- */

    function renderEditModal(): React.ReactNode
    {
        if (!editTarget) { return null; }

        return (
            <Modal show={!!editTarget} onHide={handleCloseEdit} centered dialogClassName="om-edit-modal">
                <div className="om-modal-header">
                    <div className="om-modal-header-left">
                        <div className="om-modal-icon om-modal-icon-blue">
                            <PencilSquare size={18} />
                        </div>
                        <div>
                            <h2 className="om-modal-title">Edit Organization</h2>
                            <p className="om-modal-subtitle">Update organization details and preferences</p>
                        </div>
                    </div>
                    <button className="om-modal-close" onClick={handleCloseEdit} type="button" aria-label="Close">
                        <XLg size={18} />
                    </button>
                </div>

                <div className="om-modal-body">
                    {/* Name */}
                    <div className="mb-3">
                        <label className="om-edit-label">Organization Name *</label>
                        <Form.Control
                            value={editTarget.name}
                            onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
                            isInvalid={!!editErrors.name}
                        />
                        <Form.Control.Feedback type="invalid">{editErrors.name}</Form.Control.Feedback>
                    </div>

                    {/* Type + Size */}
                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="om-edit-label">Organization Type</label>
                            <CustomDropdown
                                value={editTarget.organization_type || ''}
                                options={ORG_TYPE_OPTIONS}
                                onChange={(selected) => setEditTarget({ ...editTarget, organization_type: selected || null })}
                                placeholder="— Select type —"
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="om-edit-label">Organization Size</label>
                            <CustomDropdown
                                value={editTarget.organization_size || ''}
                                options={ORG_SIZE_OPTIONS}
                                onChange={(selected) => setEditTarget({ ...editTarget, organization_size: selected || null })}
                                placeholder="— Select size —"
                            />
                        </div>
                    </div>

                    {isSuperAdmin ? (
                        <div className="om-feature-settings mb-3">
                            <div className="om-feature-settings-title">Feature Flags</div>
                            <div className="om-feature-settings-subtitle">Enable or disable platform features for this organization.</div>

                            <div className="om-feature-settings-panel">
                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <PlayCircle size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Test Execution</div>
                                            <div className="om-feature-description">Allow execution of tests in the platform.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Test Execution">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_test_execution_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_test_execution_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_test_execution_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_test_execution_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_test_execution_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_test_execution_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>

                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <Database size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Synthetic Data</div>
                                            <div className="om-feature-description">Allow generation and use of synthetic data.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Synthetic Data">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_synthetic_data_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_synthetic_data_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_synthetic_data_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_synthetic_data_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_synthetic_data_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_synthetic_data_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>

                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <KeyFill size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Bring Your Own Key (BYO)</div>
                                            <div className="om-feature-description">Allow the organization to configure their own LLM API key.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Bring Your Own Key">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_byo_key_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_byo_key_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_byo_key_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_byo_key_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_byo_key_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_byo_key_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>

                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <Database size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Data Validation Agent</div>
                                            <div className="om-feature-description">Compare source and target data across databases and CSV uploads.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Data Validation Agent">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_data_validation_agent_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_data_validation_agent_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_data_validation_agent_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_data_validation_agent_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_data_validation_agent_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_data_validation_agent_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>

                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <Search size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Exploratory Agent</div>
                                            <div className="om-feature-description">Allow browser-led application exploration and approved test execution.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Exploratory Agent">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_exploratory_agent_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_exploratory_agent_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_exploratory_agent_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_exploratory_agent_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_exploratory_agent_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_exploratory_agent_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>

                                <div className="om-feature-row">
                                    <div className="om-feature-meta">
                                        <div className="om-feature-icon-wrap">
                                            <PlayCircle size={18} />
                                        </div>
                                        <div>
                                            <div className="om-feature-name">Ritz Reservation Demo</div>
                                            <div className="om-feature-description">Allow the fixed Ritz Res Agent reservation demonstration workflow.</div>
                                        </div>
                                    </div>

                                    <div className="om-feature-segmented" role="radiogroup" aria-label="Ritz Reservation Demo">
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${editTarget.feature_ritz_reservation_demo_enabled ? 'om-segment-on-active' : ''}`}
                                            aria-pressed={!!editTarget.feature_ritz_reservation_demo_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_ritz_reservation_demo_enabled: true })}
                                        >
                                            Enabled
                                        </button>
                                        <button
                                            type="button"
                                            className={`om-segment-btn ${!editTarget.feature_ritz_reservation_demo_enabled ? 'om-segment-off-active' : ''}`}
                                            aria-pressed={!editTarget.feature_ritz_reservation_demo_enabled}
                                            onClick={() => setEditTarget({ ...editTarget, feature_ritz_reservation_demo_enabled: false })}
                                        >
                                            Disabled
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Actions */}
                    <div className="om-modal-actions">
                        <button className="om-btn-modal-cancel" onClick={handleCloseEdit} type="button">
                            Cancel
                        </button>
                        <button
                            className="om-btn-modal-confirm om-btn-confirm-blue"
                            onClick={handleSaveEdit}
                            disabled={saving}
                            type="button"
                        >
                            {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                            Save Changes
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    /* ---------- Render: delete modal ---------- */

    function renderDeleteModal(): React.ReactNode
    {
        if (!deleteTarget) { return null; }
        const hasActiveJobs = Boolean(deletePreview?.active_job_count);

        return (
            <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
                <div className="om-modal-header">
                    <div className="om-modal-header-left">
                        <div className="om-modal-icon om-modal-icon-red">
                            <ExclamationTriangleFill size={18} />
                        </div>
                        <div>
                            <h2 className="om-modal-title">Delete Organization</h2>
                            <p className="om-modal-subtitle">Review the impact before continuing.</p>
                        </div>
                    </div>
                    <button
                        className="om-modal-close"
                        onClick={() => setDeleteTarget(null)}
                        type="button"
                        aria-label="Close"
                    >
                        <XLg size={18} />
                    </button>
                </div>

                <div className="om-modal-body">
                    <p className="om-delete-message">
                        Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                        Organization memberships, users without another organization, projects, repositories, teams, and jobs will be deleted.
                    </p>
                    <div className="om-warning-box">
                        <ExclamationTriangleFill className="om-warning-icon" />
                        <div>
                            <strong>Billing will be permanently closed without a refund.</strong>
                            <div>Active subscriptions will be cancelled immediately, unpaid invoices voided, and saved payment methods removed. Unused subscription time and LLM Usage Credits are not refunded.</div>
                        </div>
                    </div>

                    {deletePreviewLoading && <p className="om-delete-message">Checking for active jobs...</p>}
                    {!deletePreviewLoading && hasActiveJobs && (
                        <div className="om-warning-box">
                            <ExclamationTriangleFill className="om-warning-icon" />
                            <div>
                                <strong>Organization jobs are still active.</strong>
                                <div>Cancel or stop these jobs before deleting the organization: {deletePreview?.active_job_ids.join(', ')}.</div>
                            </div>
                        </div>
                    )}

                    <div className="om-modal-actions">
                        <button className="om-btn-modal-cancel" onClick={() => setDeleteTarget(null)} type="button">
                            Cancel
                        </button>
                        <button
                            className="om-btn-modal-confirm om-btn-confirm-red"
                            onClick={handleConfirmDelete}
                            disabled={saving || deletePreviewLoading || !deletePreview || hasActiveJobs}
                            type="button"
                        >
                            {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                            Delete Organization
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="om-page">
            {renderHeader()}
            {renderNotification()}
            {renderFilters()}
            {renderTable()}

            {renderCreateModal()}
            {renderEditModal()}
            {renderDeleteModal()}
        </div>
    );
};

export default OrganizationManagement;
